import { useEffect, useState, type FunctionComponent } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { exercisesActions, workoutTemplatesActions } from "@fitness-app/app-store";
import {
  type ChallengeActivityCategory,
  type ChallengeExerciseRecordingConfig,
  type ChallengeMilestone,
  ChallengeParticipantStatus,
  type ChallengeTaskConfig,
  type ChallengeTaskEntity,
  ChallengeTaskType,
  ChallengeVerification,
  getChallengeRecordingEventDates,
} from "@fitness-app/data-models/entities/Challenge";
import { type HabitTemplateEntity } from "@fitness-app/data-models/entities/Habit";
import { type ProgramWorkout } from "@fitness-app/data-models/entities/TrainingProgram";
import { createProgramWorkoutTemplate } from "@fitness-app/utils/src/programs/workoutsTemplate";

import BatchWorkoutDayBuilder from "~/modules/Trainee/BatchAddWorkout/BatchWorkoutDayBuilder";
import { supabase, useAppDispatch } from "~/store/initializeStore";
import {
  challengeActivityCategoryOptions,
  challengeEventDayOfWeekOptions as dayOfWeekOptions,
  challengeTaskTypeOptions,
  defaultVerificationByTaskType,
  verificationOptionsForTaskType,
} from "./constants";
import { formatChallengeDate, getFlaggedRecordingExercises } from "./helpers";
import { scheduleChallengeRecordingWorkouts } from "./scheduleChallengeRecordingWorkouts";

const DATE_FORMAT = "YYYY-MM-DD";

type Props = {
  open: boolean;
  challengeId: string;
  task: ChallengeTaskEntity | null;
  nextSortOrder: number;
  onClose: () => void;
};

type MilestoneFormItem = {
  value: number;
  bonus: number;
};

type TaskFormValues = {
  type: ChallengeTaskType;
  title: string;
  description?: string;
  verification: ChallengeVerification;
  points: number;
  sortOrder: number;
  activityCategory?: ChallengeActivityCategory;
  habitTemplateId?: string;
  eventDates?: Dayjs[];
  target?: number;
  unit?: string;
  dailyLimit?: number;
  milestoneThresholds?: MilestoneFormItem[];
  daysOfWeek?: number[];
  perDayTarget?: number;
  weeklyTarget?: number;
};

/** Task types whose scoring is date-driven and can be restricted to selected weekdays. */
const scheduleAwareTaskTypes = new Set([
  ChallengeTaskType.ExtraTask,
  ChallengeTaskType.NumericGoal,
  ChallengeTaskType.PlanActivity,
]);

const toFormValues = (task: ChallengeTaskEntity): TaskFormValues => {
  const milestones = (task.milestoneThresholds as ChallengeMilestone[] | null) ?? [];
  const config = (task.config as ChallengeTaskConfig | null) ?? null;

  return {
    type: task.type as ChallengeTaskType,
    title: task.title,
    description: task.description ?? undefined,
    verification: task.verification as ChallengeVerification,
    points: task.points,
    sortOrder: task.sortOrder,
    activityCategory: (task.activityCategory as ChallengeActivityCategory | null) ?? undefined,
    habitTemplateId: task.habitTemplateId ?? undefined,
    eventDates: getChallengeRecordingEventDates(config).map((date) => dayjs(date, DATE_FORMAT)),
    target: task.target ?? undefined,
    unit: task.unit ?? undefined,
    dailyLimit: task.dailyLimit ?? undefined,
    milestoneThresholds: milestones.map((milestone) => ({
      value: milestone.value,
      bonus: milestone.bonus,
    })),
    daysOfWeek: config?.daysOfWeek ?? [],
    perDayTarget: config?.perDayTarget ?? undefined,
    weeklyTarget: config?.weeklyTarget ?? undefined,
  };
};

/** Fan a new recording task's backing workout out to the challenge's current ACTIVE participants. */
const triggerRecordingBatch = async (challengeId: string, taskId: string): Promise<number> => {
  const { count, error } = await supabase
    .from("challenge_participant")
    .select("id", { count: "exact", head: true })
    .eq("challengeId", challengeId)
    .eq("status", ChallengeParticipantStatus.Active);

  if (error) {
    throw new Error(error.message);
  }

  if (!count) {
    return 0;
  }

  await scheduleChallengeRecordingWorkouts({ challengeId, taskId });

  return count;
};

export const ChallengeTaskFormModal: FunctionComponent<Props> = ({
  open,
  challengeId,
  task,
  nextSortOrder,
  onClose,
}) => {
  const [form] = Form.useForm<TaskFormValues>();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const isEdit = Boolean(task);

  const typeValue = Form.useWatch("type", form);
  // Hydration counts the day's ml, not glasses, so the threshold shows its unit.
  const isHydration = Form.useWatch("activityCategory", form) === "hydration";

  // exerciseRecording: the trainer builds the full workout participants will get (same builder as
  // "Dodaj trening do wielu klientów") and flags ONE exercise for video recording — that exercise
  // becomes the task's parentExerciseId the scoring engine matches recordings against.
  const [recordingWorkout, setRecordingWorkout] = useState<ProgramWorkout>(() => createProgramWorkoutTemplate());
  const isRecordingType = typeValue === ChallengeTaskType.ExerciseRecording;

  useEffect(() => {
    if (open && !task) {
      setRecordingWorkout(createProgramWorkoutTemplate());
    }
  }, [open, task]);

  useEffect(() => {
    if (open && isRecordingType && !isEdit) {
      void dispatch(exercisesActions.fetchExercises());
      void dispatch(workoutTemplatesActions.fetchWorkoutTemplates());
    }
  }, [open, isRecordingType, isEdit, dispatch]);

  const habitTemplatesQuery = useQuery({
    queryKey: ["challenge-habit-templates"],
    enabled: open,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_template")
        .select("id, name, type")
        .eq("archived", false)
        .order("name", { ascending: true })
        .returns<Pick<HabitTemplateEntity, "id" | "name" | "type">[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const defaultValues: Partial<TaskFormValues> = {
    type: ChallengeTaskType.ExtraTask,
    verification: ChallengeVerification.SelfReport,
    points: 10,
    sortOrder: nextSortOrder,
    milestoneThresholds: [],
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    if (task) {
      form.setFieldsValue(toFormValues(task));
    } else {
      form.resetFields();
      form.setFieldsValue(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task, form]);

  const saveTaskMutation = useMutation({
    mutationFn: async (values: TaskFormValues) => {
      const milestoneThresholds =
        values.type === ChallengeTaskType.NumericGoal
          ? (values.milestoneThresholds ?? [])
              .filter((milestone) => milestone?.value != null && milestone?.bonus != null)
              .map((milestone) => ({ value: milestone.value, bonus: milestone.bonus }))
          : null;

      const isRecording = values.type === ChallengeTaskType.ExerciseRecording;
      const eventDates = Array.from(new Set((values.eventDates ?? []).map((date) => date.format(DATE_FORMAT)))).sort();
      const flaggedExercise = isRecording && !isEdit ? getFlaggedRecordingExercises(recordingWorkout)[0] : undefined;

      const daysOfWeek =
        scheduleAwareTaskTypes.has(values.type) && values.daysOfWeek?.length ? values.daysOfWeek : null;
      const perDayTarget =
        values.type === ChallengeTaskType.PlanActivity && values.perDayTarget ? values.perDayTarget : null;
      const weeklyTarget =
        values.type === ChallengeTaskType.PlanActivity && values.weeklyTarget ? values.weeklyTarget : null;

      const scheduleConfig = {
        ...(daysOfWeek ? { daysOfWeek } : {}),
        ...(perDayTarget ? { perDayTarget } : {}),
        ...(weeklyTarget ? { weeklyTarget } : {}),
      };

      // Recording tasks persist the full built workout + date on `config` so the batch can be
      // re-applied to late joiners (assignCohort/selfJoin). On edit the workout/date are locked —
      // keep the stored config verbatim (rebuilding would desync already-scheduled recordings).
      const recordingConfig =
        isRecording && !isEdit && eventDates.length
          ? { workoutTemplate: recordingWorkout as unknown as Record<string, unknown>, eventDates }
          : null;
      const config = isRecording
        ? isEdit
          ? ((task?.config as ChallengeTaskConfig | null) ?? null)
          : recordingConfig
        : Object.keys(scheduleConfig).length
          ? scheduleConfig
          : null;

      const payload = {
        challengeId,
        type: values.type,
        title: values.title,
        description: values.description ?? null,
        verification: values.verification,
        points: values.points,
        sortOrder: values.sortOrder,
        activityCategory: values.type === ChallengeTaskType.PlanActivity ? (values.activityCategory ?? null) : null,
        habitTemplateId: values.type === ChallengeTaskType.Habit ? (values.habitTemplateId ?? null) : null,
        parentExerciseId: isRecording
          ? isEdit
            ? (task?.parentExerciseId ?? null)
            : (flaggedExercise?.exercise?.id ?? null)
          : null,
        config,
        target: values.type === ChallengeTaskType.NumericGoal ? (values.target ?? null) : null,
        unit: values.type === ChallengeTaskType.NumericGoal ? (values.unit ?? null) : null,
        dailyLimit: values.type === ChallengeTaskType.ExtraTask ? (values.dailyLimit ?? null) : null,
        milestoneThresholds,
      };

      if (task) {
        const { error } = await supabase
          .from("challenge_task")
          .update(payload as never)
          .eq("id", task.id);

        if (error) {
          throw new Error(error.message);
        }

        return;
      }

      const { data: created, error } = await supabase
        .from("challenge_task")
        .insert(payload as never)
        .select("id")
        .single<{ id: string }>();

      if (error) {
        throw new Error(error.message);
      }

      // New recording task: fan out the backing workout to the current cohort so each participant gets a
      // scheduled recording request (later joiners are covered by assignCohort/selfJoin re-application).
      // The task is already saved — a batch enqueue failure is surfaced as a warning, not a save failure.
      if (recordingConfig) {
        try {
          const queued = await triggerRecordingBatch(challengeId, created.id);
          if (queued > 0) {
            const datesCount = recordingConfig.eventDates.length;
            void message.info(
              `Zaplanowano nagrania dla ${queued} uczestniczek${datesCount > 1 ? ` (${datesCount} terminów)` : ""}`,
            );
          } else {
            void message.info(
              "Wyzwanie nie ma jeszcze aktywnych uczestniczek - trening trafi do klientek przy przypisaniu do wyzwania",
            );
          }
        } catch (batchError) {
          void message.warning(
            `Zadanie zapisane, ale nie udało się zaplanować nagrań: ${
              batchError instanceof Error ? batchError.message : "nieznany błąd"
            }`,
          );
        }
      }
    },
    onSuccess: () => {
      void message.success(isEdit ? "Zadanie zaktualizowane" : "Zadanie dodane");
      void queryClient.invalidateQueries({ queryKey: ["challenge-tasks", challengeId] });
      form.resetFields();
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać zadania: ${error.message}`);
    },
  });

  const handleTypeChange = (nextType: ChallengeTaskType) => {
    // Reset verification to a mode that actually has a data source for the new type
    // (only planActivity can be 'auto'; otherwise the engine would score 0).
    form.setFieldValue("verification", defaultVerificationByTaskType[nextType]);
  };

  const habitTemplateOptions = (habitTemplatesQuery.data ?? []).map((template) => ({
    value: template.id,
    label: template.name,
  }));

  const handleFinish = (values: TaskFormValues) => {
    // Converting an existing task to/from a recording task is unsupported: the recording workout only
    // fans out on creation, so a converted task would have no workoutTemplate (or orphan scheduled
    // recordings). The Select blocks it in the UI — this guard is a belt-and-braces safety net.
    if (
      isEdit &&
      (values.type === ChallengeTaskType.ExerciseRecording) !== (task?.type === ChallengeTaskType.ExerciseRecording)
    ) {
      void message.error("Nie można zmienić typu zadania na/z „Trening z wideo” — usuń zadanie i dodaj nowe");
      return;
    }

    if (values.type === ChallengeTaskType.ExerciseRecording && !isEdit) {
      const totalExercises =
        recordingWorkout.exercises.length + recordingWorkout.preWorkout.length + recordingWorkout.postWorkout.length;
      if (!totalExercises) {
        void message.error("Dodaj ćwiczenia do treningu");
        return;
      }
      const flagged = getFlaggedRecordingExercises(recordingWorkout);
      if (flagged.length !== 1) {
        void message.error(
          "Oznacz dokładnie jedno ćwiczenie do nagrania wideo (przełącznik „Nagranie wideo” przy ćwiczeniu)",
        );
        return;
      }
    }

    saveTaskMutation.mutate(values);
  };

  const editRecordingConfig = isEdit ? ((task?.config as ChallengeExerciseRecordingConfig | null) ?? null) : null;
  const editRecordingWorkoutName =
    editRecordingConfig?.workoutTemplate && typeof editRecordingConfig.workoutTemplate.name === "string"
      ? editRecordingConfig.workoutTemplate.name
      : null;

  return (
    <Modal
      title={isEdit ? "Edytuj zadanie" : "Dodaj zadanie"}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? "Zapisz" : "Dodaj"}
      cancelText="Anuluj"
      confirmLoading={saveTaskMutation.isLoading}
      width={isRecordingType && !isEdit ? 1000 : 560}
      destroyOnClose
    >
      <Form<TaskFormValues> form={form} layout="vertical" initialValues={defaultValues} onFinish={handleFinish}>
        <Form.Item name="type" label="Typ zadania" rules={[{ required: true, message: "Wybierz typ zadania" }]}>
          {/* Recording tasks can't be converted to/from other types (see handleFinish guard):
              editing a recording task locks the type; editing any other task hides the recording option. */}
          <Select
            options={
              isEdit
                ? challengeTaskTypeOptions.map((option) => ({
                    ...option,
                    disabled: option.value === ChallengeTaskType.ExerciseRecording,
                  }))
                : challengeTaskTypeOptions
            }
            disabled={isEdit && task?.type === ChallengeTaskType.ExerciseRecording}
            onChange={handleTypeChange}
          />
        </Form.Item>

        <Form.Item name="title" label="Nazwa" rules={[{ required: true, message: "Podaj nazwę zadania" }]}>
          <Input placeholder="np. Wykonaj 3 treningi w tygodniu" />
        </Form.Item>

        <Form.Item name="description" label="Opis">
          <Input.TextArea rows={3} placeholder="Krótki opis zadania" />
        </Form.Item>

        {typeValue === ChallengeTaskType.PlanActivity ? (
          <>
            <Form.Item
              name="activityCategory"
              label="Kategoria aktywności"
              tooltip={
                "Zaliczane automatycznie, gdy klient wykona zarejestrowaną aktywność tej kategorii " +
                "(np. zaplanowany trening). To jedyny w pełni automatyczny typ — dobry do celów typu " +
                '„3 treningi w tygodniu" (kategoria: Treningi).'
              }
              rules={[{ required: true, message: "Wybierz kategorię aktywności" }]}
            >
              <Select options={challengeActivityCategoryOptions} placeholder="Wybierz kategorię" />
            </Form.Item>
            <Space size={16} style={{ display: "flex" }}>
              <Form.Item
                name="perDayTarget"
                label="Dzienny próg (opcjonalnie)"
                style={{ flex: 1 }}
                tooltip={
                  "Dzień zalicza się dopiero od tej liczby aktywności i punktuje raz — " +
                  'np. „Wypij 3 litry wody" = kategoria Nawodnienie + próg 3000 (nawodnienie liczy się w ml). ' +
                  "Puste = punkty za każdą aktywność."
                }
              >
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  placeholder={isHydration ? "np. 3000" : "np. 5"}
                  addonAfter={isHydration ? "ml" : undefined}
                />
              </Form.Item>
              <Form.Item
                name="weeklyTarget"
                label="Cel tygodniowy (opcjonalnie)"
                style={{ flex: 1 }}
                tooltip={
                  "Punkty za każdy tydzień (pon–niedz) z co najmniej tyloma zaliczonymi dniami — " +
                  'np. „3 treningi w tygodniu" = kategoria Treningi + cel 3. ' +
                  "Łączy się z progiem dziennym."
                }
              >
                <InputNumber min={1} max={7} style={{ width: "100%" }} placeholder="np. 3" />
              </Form.Item>
            </Space>
          </>
        ) : null}

        {typeValue === ChallengeTaskType.Habit ? (
          <Form.Item
            name="habitTemplateId"
            label="Powiązany nawyk"
            rules={[{ required: true, message: "Wybierz nawyk" }]}
            tooltip="Po przypisaniu klientek do wyzwania ten nawyk zostanie im automatycznie nadany"
          >
            <Select
              options={habitTemplateOptions}
              loading={habitTemplatesQuery.isLoading}
              placeholder="Wybierz szablon nawyku"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        ) : null}

        {typeValue === ChallengeTaskType.ExerciseRecording ? (
          isEdit ? (
            // The recording batch fans out only on task creation — editing the workout/date would leave
            // already-scheduled recordings on the old exercise/date, so they are locked after save.
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
              message="Trening i terminy są zablokowane po zapisaniu"
              description={
                `${editRecordingWorkoutName ? `Trening: ${editRecordingWorkoutName}. ` : ""}` +
                `Terminy: ${
                  getChallengeRecordingEventDates(editRecordingConfig)
                    .map((date) => formatChallengeDate(date))
                    .join(", ") || "-"
                }. ` +
                "Aby zmienić trening lub terminy, usuń zadanie i dodaj nowe."
              }
            />
          ) : (
            <>
              <Form.Item
                name="eventDates"
                label="Terminy treningu z wideo"
                rules={[{ required: true, message: "Wybierz co najmniej jedną datę" }]}
                tooltip={
                  "Możesz wybrać kilka dni — trening trafi do kalendarza uczestniczek w każdym z nich. " +
                  "Punkty naliczają się za KAŻDY nagrany termin (np. 12 terminów × 10 pkt = 120 pkt do zdobycia), " +
                  "a zadanie liczy się jako ukończone po nagraniu wszystkich terminów."
                }
              >
                <DatePicker multiple style={{ width: 420 }} format="DD.MM.YYYY" maxTagCount="responsive" />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Zbuduj trening, który uczestniczki dostaną w kalendarzu"
                description={
                  "Oznacz dokładnie jedno ćwiczenie przełącznikiem „Nagranie wideo” — wyzwanie zliczy " +
                  "nagranie tego ćwiczenia. Po zapisaniu zadania trening trafi do obecnych uczestniczek, " +
                  "a późniejsze dołączające dostaną go automatycznie. Treningu i daty nie można potem zmienić."
                }
              />
              <Form.Item label="Trening" required>
                <BatchWorkoutDayBuilder
                  workout={recordingWorkout}
                  onChange={setRecordingWorkout}
                  showRecordingVideoSwitch
                />
              </Form.Item>
            </>
          )
        ) : null}

        {typeValue === ChallengeTaskType.NumericGoal ? (
          <>
            <Space size={16} style={{ display: "flex" }}>
              <Form.Item
                name="target"
                label="Cel"
                tooltip={
                  "Wartość docelowa, którą klient sam raportuje (np. liczba kroków). " +
                  "Punkty przyznawane po osiągnięciu celu, a kamienie milowe dają bonusy po przekroczeniu progów."
                }
                rules={[{ required: true, message: "Podaj wartość celu" }]}
                style={{ flex: 1 }}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="unit" label="Jednostka" style={{ flex: 1 }}>
                <Input placeholder="np. km, l, kg" />
              </Form.Item>
            </Space>

            <Form.Item label="Kamienie milowe (bonusy)">
              <Form.List name="milestoneThresholds">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {fields.map((field) => (
                      <Space key={field.key} align="baseline" style={{ width: "100%" }}>
                        <Form.Item
                          {...field}
                          name={[field.name, "value"]}
                          rules={[{ required: true, message: "Podaj próg" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber min={0} placeholder="Próg (wartość)" style={{ width: 180 }} />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "bonus"]}
                          rules={[{ required: true, message: "Podaj bonus" }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber min={0} placeholder="Bonus (pkt)" style={{ width: 160 }} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </Space>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
                      Dodaj kamień milowy
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Form.Item>
          </>
        ) : null}

        {typeValue === ChallengeTaskType.ExtraTask ? (
          <Form.Item
            name="dailyLimit"
            label="Dzienny limit zaliczeń (opcjonalnie)"
            tooltip="Maksymalna liczba zaliczeń tego zadania w jednym dniu"
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        ) : null}

        {scheduleAwareTaskTypes.has(typeValue ?? ChallengeTaskType.ExtraTask) ? (
          <Form.Item
            name="daysOfWeek"
            label="Dni tygodnia (opcjonalnie)"
            tooltip={
              "Zadanie liczy się tylko w wybrane dni (np. kroki w weekendy) — w pozostałe dni jest " +
              "zablokowane w aplikacji, a zaliczenia poza harmonogramem nie punktują. " +
              "Puste = zadanie aktywne codziennie."
            }
          >
            <Select mode="multiple" allowClear options={dayOfWeekOptions} placeholder="Codziennie" />
          </Form.Item>
        ) : null}

        <Form.Item
          name="verification"
          label="Weryfikacja"
          rules={[{ required: true, message: "Wybierz sposób weryfikacji" }]}
          tooltip={
            "Automatyczna — naliczane z zarejestrowanej aktywności klienta; dostępne tylko dla „Aktywności z planu”. " +
            "Samoocena — klient sam odhacza wykonanie. " +
            "Dowód (zdjęcie) — klient wgrywa zdjęcie. " +
            "Akceptacja trenera — zadanie czeka na Twoją akceptację. " +
            "Cele liczbowe i zadania dodatkowe nie mają źródła automatycznego — postęp raportuje klient."
          }
        >
          <Select options={verificationOptionsForTaskType(typeValue ?? ChallengeTaskType.ExtraTask)} />
        </Form.Item>

        <Space size={16} style={{ display: "flex" }}>
          <Form.Item
            name="points"
            label="Punkty"
            rules={[{ required: true, message: "Podaj liczbę punktów" }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="sortOrder"
            label="Kolejność"
            rules={[{ required: true, message: "Podaj kolejność" }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};

export default ChallengeTaskFormModal;
